import React from "react";
import PropTypes from "prop-types";
import { getVarsoUrl } from "../../common/helpers/helpers";
import { formatModeofInheritance } from "./helpers";
import ComponentPanel from "../atoms/ComponentPanel/ComponentPanel";
import { omimStringTransformer, OMIM_MAPPINGS } from "./constants";
import Accordion from "../atoms/Accordion";

const constructHeader = (item) => {
  if (!item || !Object.keys(item).length) {
    return null;
  }
  return (
    <>
      {item.title || ""}
      <>
        {item.omim_id && (
          <span className="tw-ml-2 small" data-testid="omim-gene-id">
            OMIM Id: {item.omim_id}{" "}
            <a
              className="tw-ml-2 tw-text-2xs"
              href={getVarsoUrl(`https://omim.org/entry/${item.omim_id}`)}
              target="_blank"
            >
              View this on OMIM
            </a>
          </span>
        )}
      </>
    </>
  );
};

const OmimGene = ({ data }) => {
  return data ? (
    <div className="varsome-result-fixed">
      <div className="varsome-container varsome-container__basic">
        <ComponentPanel
          title="OMIM &reg;"
          version={data?.version}
          url={getVarsoUrl("https://omim.org/")}
          titleDetailsRight={
            <div className="varsome-right">
              <small className="tw-mr-4">
                <a
                  className="tw-mr-2"
                  href="https://omim.org/help/faq"
                  target="_blank"
                >
                  OMIM FAQ
                </a>
              </small>
            </div>
          }
          content={
            <div className="varsome-inner-container">
              {data?.items && (
                <>
                  {data.items.map((item, i) => (
                    <div key={i} className="tw-mb-4">
                      <Accordion
                        title={constructHeader(item)}
                        content={
                          <>
                            {item &&
                              item?.phenotypes &&
                              item?.phenotypes.length > 0 && (
                                <table className="varsome-table varsome-table-fixed varsome-table-fixed--borderBody varsome-table-fixed--autoWidth tw-mx-6 tw-mb-6">
                                  <thead>
                                    <tr className="tw-border-t-0">
                                      <th data-testid="omim-gene-location">
                                        Location
                                      </th>
                                      <th data-testid="omim-gene-phenotype">
                                        Phenotype
                                      </th>
                                      <th data-testid="omim-gene-number">
                                        Phenotype OMIM number
                                      </th>
                                      <th data-testid="omim-gene-inheritance">
                                        Inheritance
                                      </th>
                                      <th data-testid="omim-gene-mapping-key">
                                        Phenotype mapping key
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {item.phenotypes.map((phenotype, y) => (
                                      <tr key={phenotype.phenotype_name}>
                                        {y === 0 ? (
                                          <td
                                            rowSpan={item.phenotypes.length}
                                            data-testid="omim-gene-cyto-location"
                                          >
                                            {item?.chromosome_symbol &&
                                            item.chromosome_sort ? (
                                              <a
                                                href={getVarsoUrl(
                                                  `https://omim.org/geneMap/${item?.chromosome_symbol}/${item?.chromosome_sort}?start=-3&limit=10&highlight=${item?.chromosome_sort}`
                                                )}
                                                target="_blank"
                                              >
                                                {item.computed_cyto_location}
                                              </a>
                                            ) : (
                                              item.computed_cyto_location
                                            )}
                                          </td>
                                        ) : null}
                                        <td data-testid="omim-gene-phenotype-name">
                                          {phenotype?.phenotype_name}
                                        </td>
                                        <td>
                                          <a
                                            href={getVarsoUrl(
                                              `https://omim.org/entry/${phenotype?.omim_id}`
                                            )}
                                            target="_blank"
                                          >
                                            {phenotype?.omim_id}
                                          </a>
                                        </td>
                                        <td>
                                          {phenotype?.mode_of_inheritance && (
                                            <>
                                              {phenotype?.mode_of_inheritance.map(
                                                (mode, z) => (
                                                  <span
                                                    className="abbr--custom"
                                                    key={mode}
                                                    title={mode}
                                                  >
                                                    {z === 0 ? "" : ", "}
                                                    {formatModeofInheritance(
                                                      mode
                                                    )}
                                                  </span>
                                                )
                                              )}
                                            </>
                                          )}
                                        </td>
                                        <td>
                                          <span
                                            className="abbr--custom"
                                            data-testid="omim-gene-phenotype-mapping-key"
                                            title={
                                              OMIM_MAPPINGS[
                                                phenotype?.phenotype_mapping_key
                                              ]
                                            }
                                          >
                                            {phenotype?.phenotype_mapping_key}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            {item && item?.texts && item?.texts.length > 0 && (
                              <>
                                {item.texts.map((text, x) => (
                                  <div
                                    key={x}
                                    className="tw-mx-6 tw-mb-4"
                                    data-testid="omim-gene-texts"
                                  >
                                    <Accordion
                                      title={text.title}
                                      content={omimStringTransformer(
                                        text.content,
                                        item?.references
                                      )}
                                    />
                                  </div>
                                ))}
                              </>
                            )}
                          </>
                        }
                      />
                    </div>
                  ))}
                </>
              )}
            </div>
          }
        />
      </div>
    </div>
  ) : null;
};

OmimGene.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        chromosome_sort: PropTypes.number,
        chromosome_symbol: PropTypes.string,
        computed_cyto_location: PropTypes.string,
        cyto_location: PropTypes.string,
        date_updated: PropTypes.string,
        omim_id: PropTypes.number,
        prefix: PropTypes.string,
        phenotypes: PropTypes.arrayOf(
          PropTypes.shape({
            phenotype_name: PropTypes.string,
            phenotype_mapping_key: PropTypes.number,
            omim_id: PropTypes.number,
            phenotypic_series: PropTypes.string,
            mode_of_inheritance: PropTypes.arrayOf(PropTypes.string),
          })
        ),
        references: PropTypes.arrayOf(
          PropTypes.shape({
            pub_med_references: PropTypes.arrayOf(PropTypes.number),
            reference_number: PropTypes.number,
            title: PropTypes.string,
            source: PropTypes.string,
            authors: PropTypes.string,
          })
        ),
        title: PropTypes.string,
        texts: PropTypes.arrayOf(
          PropTypes.shape({
            title: PropTypes.string,
            content: PropTypes.string,
          })
        ),
        transcript: PropTypes.string,
        type: PropTypes.string,
      })
    ),
    version: PropTypes.string,
  }),
};

OmimGene.defaultProps = {
  data: null,
};

export default OmimGene;
